import {
  BarChart as BarChartIcon,
  MoreVert,
  PieChart as PieChartIcon,
  Timeline,
} from "@mui/icons-material";
import {
  Box,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DateRangeContext from "../../../contexts/Dashboard/DateRange";
import axiosInstance from "../../../axiosInstance";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const TestChart = () => {
  const [salesData, setSalesData] = useState(null);
  const [, setLoading] = useState(true);

  const selectedBranchesString = useSelector(
    (state) => state?.branches?.branchesString
  );
  const { startDate, endDate } = useContext(DateRangeContext);
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [tabValue, setTabValue] = useState(0); // 0: daily, 1: weekly, 2: monthly, 3: yearly
  const [chartType, setChartType] = useState("area");
  const lightMode = useSelector((state) => state.mode.lightMode);

  const { t } = useTranslation();

  const viewModes = [
    { label: t("daily"), value: 0 },
    { label: t("monthly"), value: 2 },
    { label: t("yearly"), value: 3 },
  ];

  const handleChartTypeChange = (type) => {
    // Tek veri noktası varsa sadece bar chart'a izin ver
    const isSingleDataPoint = chartData.length === 1;
    if (isSingleDataPoint && type !== "bar") {
      return;
    }
    setChartType(type);
    handleMenuClose();
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getSalesData = async () => {
    const formattedStartDate = startDate.format("YYYY-MM-DD");
    const formattedEndDate = endDate.format("YYYY-MM-DD");
    try {
      setLoading(true);
      const accessToken = localStorage.getItem("accessToken");
      const token = localStorage.getItem("loginUser");
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_BASE_URL}dashboard/sales_chart?start_date=${formattedStartDate}&end_date=${formattedEndDate}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            XRefreshToken: `${token}`,
            Accounts: selectedBranchesString,
            "Content-Type": "application/json",
          },
        }
      );
      setSalesData(response.data);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getSalesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedBranchesString]);

  const getFormattedData = () => {
    if (!salesData) return [];

    switch (tabValue) {
      case 0: // Günlük
        return salesData.daily_sales.map((item) => ({
          name: item.day,
          value: item.total_sales,
        }));
      case 2: // Aylık
        return salesData.monthly_sales.map((item) => ({
          name: `${item.month} '${item.year}`,
          value: item.total_sales,
        }));
      case 3: // Yıllık
        return salesData.yearly_sales.map((item) => ({
          name: `${item.year}`,
          value: item.total_sales,
        }));
      default:
        return []; // Eğer haftalık da eklenecekse buraya eklenir
    }
  };

  const chartData = getFormattedData();


  const isSingleDataPoint = chartData.length === 1;

  // Tek veri noktası olduğunda otomatik bar chart seç
  useEffect(() => {
    if (isSingleDataPoint) {
      setChartType("bar");
    }
  }, [isSingleDataPoint]);

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          p: 2,
          pb: 1,
          borderBottom: `1px dashed ${lightMode ? `#e9ecee` : `#333e47`} `,
        }}
      >
        <Typography
          variant="h5"
          color={lightMode ? theme.palette.customRed.orange : "#ffffff"}
          sx={{
            fontWeight: "700",
            mr: "auto",
            textTransform: 'uppercase'
          }}
        >
          {t("sales")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <FormControl variant="standard" size="small">
            <Select
              value={tabValue}
              onChange={(e) => setTabValue(Number(e.target.value))}
              disableUnderline
              sx={{
                fontWeight: "bold",
                fontSize: "1rem",
                color: "#9ba4b5",
                "& .MuiSelect-icon": {
                  color: "#9ba4b5",
                },
              }}
            >
              {viewModes.map((mode) => (
                <MenuItem key={mode.value} value={mode.value}>
                  {mode.label.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            sx={{
              marginLeft: "1rem",
            }}
            onClick={handleMenuClick}
          >
            <MoreVert />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => handleChartTypeChange("area")}>
              <PieChartIcon sx={{ mr: 1 }} /> {t("areaChart")}
            </MenuItem>
            <MenuItem onClick={() => handleChartTypeChange("line")}>
              <Timeline sx={{ mr: 1 }} /> {t("lineChart")}
            </MenuItem>
            <MenuItem onClick={() => handleChartTypeChange("bar")}>
              <BarChartIcon sx={{ mr: 1 }} /> {t("barChart")}
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box
        sx={{
          height: 400,
          px: 2,
          py: 4,
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          {chartType === "line" ? (
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                angle={
                  chartData.reduce((acc, cur) => acc + cur.name.length, 0) > 80
                    ? -45
                    : 0
                }
                textAnchor={
                  chartData.reduce((acc, cur) => acc + cur.name.length, 0) > 80
                    ? "end"
                    : "middle"
                }
                tick={{
                  fill: lightMode ? "#919eab" : "#ffffff",
                  fontSize: 12,
                }}
                tickMargin={10}
                height={
                  chartData.reduce((acc, cur) => acc + cur.name.length, 0) > 80
                    ? 70
                    : 40
                }
                stroke={lightMode ? "#919eab" : "#ffffff"}
              />
              <YAxis
                tick={{
                  fill: lightMode ? "#919eab" : "#ffffff",
                  fontSize: 12,
                }}
                tickMargin={10}
              />
              <Tooltip formatter={(value) => [`£${value}`, t("sales")]} />
              <Legend />
              <Line
                type="monotone"
                dataKey="value"
                name={t("sales")}
                stroke={theme.palette.primary.main}
                activeDot={{ r: 8 }}
                strokeWidth={2}
              />
            </LineChart>
          ) : chartType === "bar" ? (
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                angle={
                  chartData.reduce((acc, cur) => acc + cur.name.length, 0) > 80
                    ? -45
                    : 0
                }
                textAnchor={
                  chartData.reduce((acc, cur) => acc + cur.name.length, 0) > 80
                    ? "end"
                    : "middle"
                }
                tick={{
                  fill: lightMode ? "#919eab" : "#ffffff",
                  fontSize: 12,
                }}
                tickMargin={10}
                height={
                  chartData.reduce((acc, cur) => acc + cur.name.length, 0) > 80
                    ? 70
                    : 40
                }
                stroke={lightMode ? "#919eab" : "#ffffff"}
              />
              <YAxis
                tick={{
                  fill: lightMode ? "#919eab" : "#ffffff",
                  fontSize: 12,
                }}
                tickMargin={10}
              />
              <Tooltip formatter={(value) => [`£${value}`, t("sales")]} />
              <Legend />
              <Bar
                dataKey="value"
                name={t("sales")}
                fill={theme.palette.primary.main}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          ) : (
            <AreaChart
              data={chartData}
              margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
            >
              <defs>
                <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="20%" stopColor="#1D3557" stopOpacity={1} />
                  <stop offset="50%" stopColor="#87afd2" stopOpacity={1} />
                  <stop offset="100%" stopColor="#fff" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                angle={
                  chartData.reduce((acc, cur) => acc + cur.name.length, 0) > 80
                    ? -45
                    : 0
                }
                textAnchor={
                  chartData.reduce((acc, cur) => acc + cur.name.length, 0) > 80
                    ? "end"
                    : "middle"
                }
                tick={{
                  fill: lightMode ? "#919eab" : "#ffffff",
                  fontSize: 12,
                }}
                tickMargin={10}
                height={
                  chartData.reduce((acc, cur) => acc + cur.name.length, 0) > 80
                    ? 70
                    : 40
                }
                stroke={lightMode ? "#919eab" : "#ffffff"}
              />
              <YAxis
                tick={{
                  fill: lightMode ? "#919eab" : "#ffffff",
                  fontSize: 12,
                }}
                tickMargin={10}
              />
              <Tooltip formatter={(value) => [`£${value}`, t("sales")]} />
              <Legend />
              <Area
                type="monotone"
                dataKey="value"
                name={t("sales")}
                stroke={theme.palette.primary.main}
                strokeWidth={3.5}
                fill="url(#colorSales)"
                activeDot={{ r: 6 }}
              />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default TestChart;
